import React from 'react';
import { Code, Zap, Database, TrendingUp, Cloud, Cpu, Shield, Sparkles, Rocket, Settings, Globe, Smartphone, ArrowRight } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';
import Reveal from './Reveal';

const iconMap = {
  Code,
  Zap,
  Database,
  TrendingUp,
  Cloud,
  Cpu,
  Shield,
  Sparkles,
  Rocket,
  Settings,
  Globe,
  Smartphone,
};

const FALLBACK = {
  section_title: 'Einfach erklärt: Ihre Firma läuft digital – fast von selbst',
  section_subtitle: 'Das übernehmen wir für Sie – ganz ohne Fachbegriffe',
  items: [],
  steps_title: "So einfach geht's",
  steps_subtitle: 'In drei Schritten zu Ihrer Lösung',
  highlights: [],
};

const Services = () => {
  const data = useContent('services', FALLBACK);
  const { t } = useLang();

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-24 px-6 bg-[#F5F5F7]">
      <div className="max-w-[1080px] mx-auto">
        <Reveal className="text-center mb-16">
          <p className="text-xs font-semibold text-[#0071E3] tracking-widest uppercase mb-4">
            {t('services.eyebrow')}
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] mb-4 tracking-tight" data-testid="services-title">
            {data.section_title}
          </h2>
          <p className="text-xl text-[#86868B] max-w-2xl mx-auto" data-testid="services-subtitle">
            {data.section_subtitle}
          </p>
        </Reveal>

        <Reveal delay={100}>
        <div className="grid md:grid-cols-3 gap-6">
          {(data.items || []).map((service) => {
            const IconComponent = iconMap[service.icon] || Code;
            return (
              <div
                key={service.id}
                className="bg-white rounded-3xl overflow-hidden hover:shadow-md transition-shadow duration-300 flex flex-col"
                data-testid={`service-card-${service.id}`}
              >
                {service.image ? (
                  <div className="h-44 overflow-hidden border-b border-[#F5F5F7]">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                ) : (
                  <div className="pt-8 px-8">
                    <IconComponent className="w-12 h-12 text-[#0071E3]" strokeWidth={1.5} />
                  </div>
                )}
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">{service.title}</h3>
                  <p className="text-[#86868B] leading-relaxed flex-1">{service.description}</p>
                  {service.example && (
                    <button
                      onClick={scrollToProjects}
                      className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#0071E3] hover:underline text-left"
                      data-testid={`service-example-${service.id}`}
                    >
                      {t('services.example')} {service.example}
                      <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </Reveal>

        {(data.highlights || []).length > 0 && (
          <Reveal className="mt-24">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] mb-3 tracking-tight" data-testid="services-steps-title">
                {data.steps_title || "So einfach geht's"}
              </h2>
              {data.steps_subtitle && (
                <p className="text-lg text-[#86868B]">{data.steps_subtitle}</p>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {data.highlights.map((h, idx) => (
                <div key={idx} className="bg-white rounded-3xl p-8" data-testid={`service-highlight-${idx}`}>
                  <div className="w-10 h-10 rounded-full bg-[#0071E3] text-white flex items-center justify-center font-semibold mb-5">
                    {idx + 1}
                  </div>
                  <div className="text-xl font-semibold text-[#1D1D1F] mb-2">{h.title}</div>
                  <p className="text-[#86868B] leading-relaxed">{h.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default Services;
